/**
 * The point of no return, and everything a person needs before reaching it.
 *
 * This panel exists so that nobody presses Schedule without having been told
 * four things:
 *
 *   1. **how many people this reaches on its channel** — 526 by email, not the
 *      1,278 in the segment;
 *   2. **how many of them will actually receive it** — in this environment,
 *      usually one or two, because the send guard only delivers to allow-listed
 *      addresses. A person who schedules and then reads "1 sent, 1,277 skipped"
 *      without warning will think the product is broken. It is not: that is the
 *      guard doing the one job it exists for;
 *   3. **everything that still blocks it**, in the API's own words. The verdict
 *      is `campaignScheduleIssues` in `@wend/shared`, computed by the API and
 *      returned as `readiness`. This screen greys its button from that object
 *      and never re-derives the rule — the way two implementations of "can this
 *      send" drift is that one of them stops checking the template status;
 *   4. **slice 18: what the list looks like, and how the send will be split.**
 *
 * ## The fourth thing, and why it does not touch the button
 *
 * `fetchCampaignListSafety` is read here rather than on the campaign's own
 * request because this is the moment of choice: a health verdict a person reads
 * *after* scheduling has cost them the decision it exists to inform. It is its
 * own `useAsyncData` for the same reason `CampaignResultsPanel` has one — a
 * different question at different tables, and no other screen wants it.
 *
 * **It is advisory, and the Schedule button does not read it.** That is the
 * hardest call on this panel and it is the contract's, argued at length in
 * `LIST_HEALTH_VERDICT_VALUES`: a pre-flight that refuses is a pre-flight people
 * route around, which is the quiet-hours lesson — *a rule that refuses the
 * common case to prevent the rare one gets worked around rather than heeded*.
 * Even `do_not_send` schedules. What the verdict changes is the **plan**: a
 * worried verdict produces smaller batches and longer waits, and the automatic
 * halt is what actually stops a bad send.
 *
 * Gating the button here would also have been this panel's own recorded
 * mistake — re-deriving a rule the API owns. `canSchedule` is the server's
 * verdict and stays the only thing the button reads.
 *
 * **A failed check is not a clean list.** The card shows `ErrorState` with the
 * request id and a retry, and the confirmation step says outright that it cannot
 * tell how the send would be split — because without that line the confirmation
 * sentence quietly implies one single send.
 *
 * ## The confirmation gained a clause it could not do without
 *
 * "This will send 526 messages as soon as the sender picks it up" stops being
 * true the moment a warm-up plan exists: 102 go out and the rest waits an hour.
 * `warmUpFirstBatchSentence` is that correction, and it sits inside the
 * confirmation rather than only on the card above it, because the card is what a
 * person read a minute ago and the confirmation is what they are agreeing to.
 *
 * **Two issues are deliberately not listed here.** The two send-guard advisories
 * are rendered by `SendGuardNotice`, which says the same thing with this
 * campaign's own numbers in it; printing both would be telling somebody the same
 * thing twice in two different tones. And `quiet_hours` is re-derived locally,
 * because the server judged it against the time the campaign was last saved with
 * and the whole purpose of this panel is to let that time be changed. It is
 * re-derived with `isQuietHour` — the same function the API used — so the two
 * cannot disagree about what counts as the middle of the night.
 *
 * Scheduling is confirmed in two steps, inline. What the confirmation says is
 * not "are you sure" but *what will happen*: how many messages, to how many
 * people, at what time, and how many will be skipped instead.
 *
 * ## Slice 17, and the one word this panel did not have
 *
 * **The confirmation was not shortened and is not shortenable.** Everything
 * inside it is the second kind of text in slice 17's rule — a person reading only
 * this card and pressing the button is committing to a send, so each clause there
 * is a thing they would otherwise conclude wrongly. What that step gained is
 * "cannot itself be undone" on the cancel sentence: it already said a scheduled
 * campaign can still be cancelled, which is a reassurance, and left out that
 * cancelling is the other one-way door. Both directions or neither.
 *
 * **The quiet-hours warning read the channel for the first time.** It is the one
 * real defect this slice found on this file, and it is the shape slice 16 warned
 * about: a sentence that was true when it was written, on a panel that later
 * grew a second channel. `NewCampaignPage` branches on the channel for the same
 * warning; this panel — the one where the time is actually committed — told a
 * WhatsApp campaign that overnight mail draws spam complaints and gets an *AWS*
 * account suspended. Wrong provider, wrong mechanism, and it would have sent
 * somebody to the wrong console. The two screens now make the same argument with
 * the same stakes named, and both are shorter than the one that was wrong.
 *
 * **The card description lost its account of itself.** "Everything it will do is
 * on this card before the button is" is an argument for the design of the card,
 * addressed to whoever builds the next one; it is in this header instead. What
 * replaced it is the fact a person needs before they read anything else — this is
 * the only action in Wend that puts a message in a real inbox — plus the promise
 * of a confirmation step, which used to sit in small type beside the button and
 * is more useful before the reader has scrolled past the blockers than after.
 */
import {
  isQuietHour,
  scheduleCampaignRequestSchema,
  type Campaign,
  type CampaignScheduleIssueCode,
  type CampaignScheduleReadiness,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Spinner,
  useToast,
} from '@wend/ui';
import { CalendarClock, Clock, Send } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CampaignReachPanel } from '../../components/CampaignReach';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { SendGuardNotice } from '../../components/SendGuardNotice';
import { ScheduleHoldoutControl } from './ScheduleHoldoutControl';
import { SchedulePressureNotice } from './SchedulePressureNotice';
import {
  fetchCampaignListSafety,
  scheduleCampaign,
  type CampaignWithReadinessResponse,
} from '../../lib/api/campaigns';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import {
  CAMPAIGN_ISSUE_TITLE,
  browserTimeZone,
  fromDateTimeLocalValue,
  issueTone,
  readSendGuard,
  scheduleTimeLabel,
  sortIssues,
  toDateTimeLocalValue,
} from '../../lib/campaign-display';
import { validate, type FormErrors } from '../../lib/form';
import { formatCount, pluralise } from '../../lib/format';
import {
  LIST_CHECK_UNAVAILABLE_SENTENCE,
  warmUpFirstBatchSentence,
} from '../../lib/list-safety-display';
import { useAsyncData } from '../../lib/use-async-data';
import { ListHealthPanel } from './ListHealthPanel';
import { WarmUpPlanPanel } from './WarmUpPlanPanel';

/**
 * Said by `SendGuardNotice` instead, with the numbers. Kept as a set rather than
 * two inline comparisons so that adding a third guard advisory to the contract
 * has one obvious place to be handled.
 */
const RENDERED_ELSEWHERE = new Set<CampaignScheduleIssueCode>([
  'send_allowlist_empty',
  'send_guard_restricts_delivery',
  // Re-derived below against the time actually chosen on this screen.
  'quiet_hours',
]);

type Timing = 'now' | 'at';

export interface ScheduleCampaignPanelProps {
  campaign: Campaign;
  readiness: CampaignScheduleReadiness;
  /** The campaign and its readiness, as the schedule left them. */
  onScheduled: (response: CampaignWithReadinessResponse) => void;
}

export function ScheduleCampaignPanel({
  campaign,
  readiness,
  onScheduled,
}: ScheduleCampaignPanelProps) {
  const { toast } = useToast();

  // The wall clock typed below is interpreted by the platform in this zone, so
  // it is the only zone the campaign can honestly say the time was chosen in —
  // even if the draft was created somewhere else.
  const zone = useMemo(browserTimeZone, []);

  const [timing, setTiming] = useState<Timing>(campaign.scheduledAt === null ? 'now' : 'at');
  const [when, setWhen] = useState(() =>
    campaign.scheduledAt === null ? '' : toDateTimeLocalValue(new Date(campaign.scheduledAt)),
  );
  const [confirming, setConfirming] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  // Slice 59: rest people at Critical message pressure for this send. Off
  // unless a person turns it on; see SchedulePressureNotice.
  const [restCritical, setRestCritical] = useState(false);
  // Slice 61: the share held back to measure the campaign. 0, off, unless chosen.
  const [holdoutPercent, setHoldoutPercent] = useState(0);

  const chosen = timing === 'now' ? null : fromDateTimeLocalValue(when);
  const guard = readSendGuard(readiness.sendGuard, readiness.reach);
  const blocked = !readiness.canSchedule;

  // Advisory, and read on the screen where the decision is taken. It does not
  // feed `blocked`: see the header.
  const safety = useAsyncData(
    (signal) => fetchCampaignListSafety(campaign.id, signal, { restCritical, holdoutPercent }),
    [campaign.id, restCritical, holdoutPercent],
  );

  // Advice the server produced about facts this screen cannot change — the
  // sandbox, the account, the template. Its own two guard advisories and its
  // quiet-hours verdict are handled separately; see the header.
  const issues = sortIssues(readiness.issues).filter((issue) => !RENDERED_ELSEWHERE.has(issue.code));

  // Quiet hours for the time actually on screen, with the shared rule.
  const quietHours = chosen !== null && isQuietHour(chosen, zone.timezone);

  function reset(): void {
    setConfirming(false);
    setErrors({});
    setRequestId(undefined);
  }

  async function onSchedule(): Promise<void> {
    if (pending) return;

    setRequestId(undefined);

    // "Send now" is the current instant, deliberately: there is no separate
    // immediate-send path, because two send paths mean one of them is
    // under-tested and it will be the one carrying the unusual message.
    const instant = timing === 'now' ? new Date() : chosen;
    if (instant === null) {
      setErrors({ scheduledAt: 'Pick a date and a time.' });
      setConfirming(false);
      return;
    }

    const result = validate(scheduleCampaignRequestSchema, {
      scheduledAt: instant.toISOString(),
      timezone: zone.timezone,
      ...(restCritical ? { restCritical: true } : {}),
      ...(holdoutPercent > 0 ? { holdoutPercent } : {}),
    });

    if (!result.ok) {
      setErrors(result.errors);
      setConfirming(false);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      const response = await scheduleCampaign(campaign.id, result.data);
      toast({
        tone: 'success',
        title: 'Campaign scheduled',
        description: `${pluralise(response.campaign.progress.counts.pending, 'message')} to send, ${formatCount(response.campaign.progress.counts.skipped)} skipped.`,
      });
      onScheduled(response);
    } catch (error) {
      // A refusal carries every blocker, keyed by its issue code — so a person
      // fixes all of them rather than one per attempt.
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setConfirming(false);
      setPending(false);
    }
  }

  const refusals = refusedIssues(errors);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schedule this campaign</CardTitle>
        <CardDescription>
          The only action in Wend that puts a message in a real inbox. It builds the recipient list
          and hands it to the sender; you will be asked to confirm, with the numbers, first.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error" requestId={requestId}>
            {errors[ROOT_FIELD]}
          </FormAlert>
        )}

        {refusals.length > 0 && (
          <ul className="flex flex-col gap-2.5">
            {refusals.map((refusal) => (
              <li key={refusal.code}>
                <Callout tone="bad" title={CAMPAIGN_ISSUE_TITLE[refusal.code]} live="assertive">
                  {refusal.message}
                </Callout>
              </li>
            ))}
          </ul>
        )}

        <CampaignReachPanel reach={readiness.reach} sendGuard={readiness.sendGuard} />

        <SendGuardNotice guard={readiness.sendGuard} reach={readiness.reach} />


        {issues.length > 0 && (
          <ul className="flex flex-col gap-2.5">
            {issues.map((issue) => (
              <li key={issue.code}>
                <Callout tone={issueTone(issue)} title={CAMPAIGN_ISSUE_TITLE[issue.code]}>
                  {issue.message}
                </Callout>
              </li>
            ))}
          </ul>
        )}

        {/* Slice 59. Below the issues, because an optional choice must not sit
            above a reason the schedule will be refused. */}
        <SchedulePressureNotice
          segmentId={campaign.segment.id}
          channel={campaign.channel}
          restCritical={restCritical}
          onRestCriticalChange={setRestCritical}
          disabled={pending}
        />

        {/* Slice 61. A choice about who this campaign is for, beside resting. */}
        <ScheduleHoldoutControl
          percent={holdoutPercent}
          onPercentChange={setHoldoutPercent}
          heldBack={safety.data?.heldBack ?? null}
          disabled={pending}
        />

        {/* THE LIST, BEFORE THE TIME AND BEFORE THE BUTTON. A verdict read after
            scheduling has cost the decision it exists to inform. */}
        <div className="flex flex-col gap-6 border-t border-line pt-5">
          {safety.data === null ? (
            safety.loadingFirst ? (
              <div className="flex items-center gap-3 text-[0.92rem] text-muted">
                <Spinner size="md" label={null} />
                <span role="status">Checking this list…</span>
              </div>
            ) : (
              <ErrorState
                error={safety.error}
                title="Could not check this list"
                onRetry={safety.reload}
                className="flex flex-col items-start gap-3"
              />
            )
          ) : (
            <>
              <ListHealthPanel health={safety.data.health} />
              <WarmUpPlanPanel
                plan={safety.data.plan}
                channel={campaign.channel}
                willAttempt={safety.data.willAttempt}
                reachable={safety.data.health.signals.total}
                warmUpEnforced={safety.data.warmUpEnforced}
              />
            </>
          )}
        </div>

        <div className="flex max-w-xl flex-col gap-5 border-t border-line pt-5">
          <fieldset className="flex flex-col gap-2.5" disabled={pending}>
            <legend className="mb-1.5 text-[0.85rem] font-medium text-text">When</legend>

            <TimingChoice
              checked={timing === 'now'}
              onSelect={() => {
                setTiming('now');
                reset();
              }}
              label="As soon as possible"
              description="Scheduled for right now. The sender picks it up on its next pass, usually within seconds."
            />

            <TimingChoice
              checked={timing === 'at'}
              onSelect={() => {
                setTiming('at');
                reset();
              }}
              label="At a specific time"
              description={`In ${zone.timezone}${zone.resolved ? '' : ' — this browser would not say which timezone it is in, so Wend has assumed the default'}.`}
            />
          </fieldset>

          {timing === 'at' && (
            <FormField label="Date and time" error={errors.scheduledAt}>
              <Input
                type="datetime-local"
                value={when}
                min={toDateTimeLocalValue(new Date())}
                disabled={pending}
                onChange={(event) => {
                  setWhen(event.target.value);
                  reset();
                }}
              />
            </FormField>
          )}

          {/*
           * THE CHANNEL IS READ HERE, AND IT WAS NOT BEFORE.
           *
           * This callout was email-only prose on a panel that schedules both
           * channels: a WhatsApp campaign timed for 2am was warned about spam
           * complaints and a suspended AWS account, about a message AWS will
           * never see. The create screen branched on the channel and this one did
           * not, so the two screens gave different accounts of the same decision
           * — and the wrong one was on the screen where the decision is actually
           * taken. What is at stake genuinely differs: on WhatsApp it is Meta's
           * quality rating and the messaging limit that follows it.
           */}
          {quietHours && (
            <Callout tone="warn" title="That lands in the middle of the night" live="polite">
              {campaign.channel === 'whatsapp'
                ? 'A message that wakes somebody up gets blocked, and blocks are what Meta rates your number on — a red rating cuts your messaging limit and can pause the number. A warning, not a refusal.'
                : 'Marketing mail delivered overnight draws more spam complaints, and complaints are what get a sending account suspended — under BYO, your own AWS account. A warning, not a refusal.'}
            </Callout>
          )}
        </div>

        {confirming ? (
          <div className="flex flex-col gap-3 rounded-base border border-line-strong bg-white/[0.02] px-4 py-4">
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
              {confirmationSentence({
                deliverable: guard.deliverable,
                reachable: guard.reachable,
                whenLabel:
                  timing === 'now'
                    ? 'as soon as the sender picks it up'
                    : chosen === null
                      ? 'at the time above'
                      : `at ${scheduleTimeLabel(chosen.toISOString(), zone.timezone)}`,
              })}
            </p>

            {/* Without one of these the sentence above implies a single send,
                which stops being true the moment a plan exists. Three branches,
                because "still checking" and "could not check" are different
                statements and only one of them is a fault. */}
            {safety.loadingFirst ? (
              <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted" role="status">
                Checking this list…
              </p>
            ) : safety.data === null ? (
              <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
                {LIST_CHECK_UNAVAILABLE_SENTENCE}
              </p>
            ) : (
              safety.data.plan !== null && (
                <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
                  {warmUpFirstBatchSentence(safety.data.plan)}
                </p>
              )
            )}

            <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
              The campaign can no longer be edited after this. You can still cancel it — that stops
              whatever has not gone yet, and cannot itself be undone.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                loading={pending}
                onClick={() => {
                  void onSchedule();
                }}
              >
                <Send aria-hidden="true" className="size-4" />
                {pending ? 'Scheduling' : 'Yes, schedule it'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Not yet
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <Button
              type="button"
              disabled={blocked}
              onClick={() => {
                setConfirming(true);
              }}
            >
              <CalendarClock aria-hidden="true" className="size-4" />
              Schedule
            </Button>

            {blocked && (
              <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
                Fix what is listed above and this becomes available. The same checks run again on the
                server, so nothing goes out around them.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Wording                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * What the confirmation says.
 *
 * It leads with the number of messages that will actually be handed to Amazon,
 * because that is the number the person is authorising. The reach is the second
 * clause, and the gap between the two is stated rather than left to subtraction.
 */
function confirmationSentence({
  deliverable,
  reachable,
  whenLabel,
}: {
  deliverable: number | null;
  reachable: number;
  whenLabel: string;
}): string {
  if (deliverable === null) {
    return `This will build the recipient list for the ${pluralise(reachable, 'person', 'people')} this campaign reaches and send ${whenLabel}. Everyone not on the send allow-list will be skipped with a reason.`;
  }

  if (deliverable === 0) {
    return `This will build the recipient list and send ${whenLabel}. Nobody will receive anything: none of the ${pluralise(reachable, 'person', 'people')} this campaign reaches is on the send allow-list, so all of them will be skipped with a reason you can read afterwards.`;
  }

  const skipped = Math.max(0, reachable - deliverable);
  const tail =
    skipped === 0
      ? ''
      : ` The other ${pluralise(skipped, 'person', 'people')} this campaign reaches will be skipped, each carrying the reason.`;

  return `This will send ${pluralise(deliverable, 'message')} ${whenLabel}.${tail}`;
}

/**
 * The blockers the API refused with, recovered from the error's field errors.
 *
 * `scheduleRefused` keys each one by its `CampaignScheduleIssueCode`, so a
 * refusal reads as the same list of problems this panel was already showing —
 * in the same words, from the same rule set. Anything whose key is not a known
 * code is left to the form-level alert rather than dropped.
 */
function refusedIssues(errors: FormErrors): Array<{ code: CampaignScheduleIssueCode; message: string }> {
  const known = Object.keys(CAMPAIGN_ISSUE_TITLE) as CampaignScheduleIssueCode[];
  return known
    .filter((code) => errors[code] !== undefined)
    .map((code) => ({ code, message: errors[code] ?? '' }));
}

/* -------------------------------------------------------------------------- */
/* Timing                                                                      */
/* -------------------------------------------------------------------------- */

function TimingChoice({
  checked,
  onSelect,
  label,
  description,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-base border border-line bg-white/[0.02] px-3.5 py-3 hover:border-line-strong">
      <input
        type="radio"
        name="schedule-timing"
        className="mt-1 size-4 shrink-0 accent-brand-b"
        checked={checked}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-[0.9rem] text-text">
          <Clock aria-hidden="true" className="size-3.5 text-muted" />
          {label}
        </span>
        <span className="mt-0.5 block text-[0.82rem] leading-snug text-muted">{description}</span>
      </span>
    </label>
  );
}
